import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import PropertyGeneralDataComponent from './PropertyGeneralDataComponent';
import PropertyValuesComponent from './PropertyValuesComponent';
import { PROPERTIES_QUERY } from 'features/properties/components/list/queries';
import { PROPERTY_VALUE_TYPES_QUERY, PROPERTY_VALUES_QUERY, PROPERTY_DETAILS } from '../../queries';
import { SAVE_PROPERTY_GENERAL_DATA, SAVE_PROPERTY_VALUE, DELETE_PROPERTY_VALUE, SAVE_PROPERTY_VALUES } from '../../mutations';
import { PROPERTY_GENERAL_DATA_SAVED, PROPERTY_VALUE_REMOVED, PROPERTY_VALUE_SAVED, PROPERTY_VALUES_SAVED } from '../../subscriptions';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { required, shape, Validator, Success, stopOnFirstFailure, Failure, ValidationError } from '@totalsoft/pure-validations';
import i18next from 'i18next';
import { isDirty as checkDirtyInfo } from '@totalsoft/change-tracking';
import { editItemPropertyVar } from 'features/items/edit/variables';
import { pvtRange, pvtList, pvtUnspecified } from './constants';
import { emptyArray } from 'utils/constants';
import { get, set, sequence, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import {} from 'hooks/apolloWrappers';
import { useToast } from 'hooks/toasts/useToast';
import { useClientQuery } from 'hooks/apollo';
export const uniquePropertyOnItemValidator = (itemProperties, isNewProperty) =>
  Validator(content => {
    if (!isNewProperty || !itemProperties || itemProperties?.length === 0) return Success;
    return itemProperties.some(x => x.propertyId === content)
      ? Failure(ValidationError(i18next.t('Items.Properties.UniquePropertyValidation')))
      : Success;
  });

const itemPropertyValidator = (itemProperties, isNewProperty) =>
  shape({
    propertyId: [required, uniquePropertyOnItemValidator(itemProperties, isNewProperty)] |> stopOnFirstFailure,
    propertyValueTypeId: required
  });

const ItemPropertyEditContainer = ({ itemProperties }) => {
  const { t } = useTranslation();
  const editItemProperty = useReactiveVar(editItemPropertyVar);
  const addToast = useToast();
  const clientQuery = useClientQuery();

  // #region state
  const [itemPropertyLens, itemPropertyDirtyInfo, resetItemPropertyLens] = useChangeTrackingLens({
    ...editItemProperty.property,
    values: []
  });
  const isNewProperty = useMemo(() => editItemProperty.isNew, [editItemProperty.isNew]);
  const validator = useMemo(() => itemPropertyValidator(itemProperties, isNewProperty), [isNewProperty, itemProperties]);
  const [validation, validate] = useDirtyFieldValidation(validator);
  const [propertyDetails, setPropertyDetails] = useState(emptyArray);
  const isDirty = useMemo(() => checkDirtyInfo(itemPropertyDirtyInfo), [itemPropertyDirtyInfo]);
  // #endregion

  // #region queries
  const { data: propertiesData } = useQuery(PROPERTIES_QUERY);

  const { data: propertyValueTypesData } = useQuery(PROPERTY_VALUE_TYPES_QUERY);

  useQuery(PROPERTY_VALUES_QUERY, {
    variables: {
      itemId: itemPropertyLens.itemId |> get,
      propertyId: itemPropertyLens.propertyId |> get
    },
    skip: !(itemPropertyLens.itemId |> get) || !((itemPropertyLens.propertyValueTypeCode |> get) in { pvtRange, pvtList }),
    onCompleted: data => {
      resetItemPropertyLens(prev => ({ ...prev, values: data.propertyValues }));
    }
  });
  // #endregion

  // #region mutations
  const [savePropertyGeneralData] = useMutation(SAVE_PROPERTY_GENERAL_DATA, {
    onError: () => {
      (itemPropertyLens.saving |> set)(false);
    }
  });

  const [savePropertyValue] = useMutation(SAVE_PROPERTY_VALUE);

  const [deletePropertyValue] = useMutation(DELETE_PROPERTY_VALUE);

  const [savePropertyValues] = useMutation(SAVE_PROPERTY_VALUES);
  // #endregion

  // #region subscriptions
  useSubscription(PROPERTY_GENERAL_DATA_SAVED, {
    onData: ({
      data: {
        data: { itemPropertyGeneralDataSaved }
      },
      client
    }) => {
      addToast(t('Items.Properties.PropertySaved'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'itemProperties' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'propertyValues' });
      client.cache.gc();
      resetItemPropertyLens(prev => ({ ...itemPropertyGeneralDataSaved.itemProperty, values: prev.values, saving: false }));
      editItemPropertyVar({ open: true, property: { ...itemPropertyGeneralDataSaved.itemProperty }, isNew: false });
    }
  });

  useSubscription(PROPERTY_VALUE_SAVED, {
    onData: ({ data: { data }, client }) => {
      const propertyValue = data.propertyValueSaved.propertyValue;
      addCachedDetail(
        client,
        { name: 'propertyValues', value: PROPERTY_VALUES_QUERY },
        { itemId: itemPropertyLens.itemId |> get, propertyId: itemPropertyLens.propertyId |> get },
        x => x.propertyValueId === propertyValue.propertyValueId,
        propertyValue,
        ['propertyValueId']
      );
      resetItemPropertyLens(prev => ({
        ...prev,
        values: prev.values.map(x => (x.propertyValueId === null ? { ...propertyValue, inEdit: false, editValue: propertyValue.value } : x))
      }));
      addToast(t('Items.Values.ValueSaved'), 'success');
    }
  });

  useSubscription(PROPERTY_VALUE_REMOVED, {
    onData: ({
      data: {
        data: { propertyValueRemoved }
      },
      client
    }) => {
      removeCachedDetail(
        client,
        { name: 'propertyValues', value: PROPERTY_VALUES_QUERY },
        { itemId: itemPropertyLens.itemId |> get, propertyId: itemPropertyLens.propertyId |> get },
        x => x.propertyValueId === propertyValueRemoved.propertyValueId,
        ['propertyValueId']
      );
      resetItemPropertyLens(prev => ({
        ...prev,
        values: prev.values.filter(x => x.propertyValueId !== propertyValueRemoved.propertyValueId)
      }));
      addToast(t('Items.Values.ValueRemoved'), 'success');
    }
  });

  useSubscription(PROPERTY_VALUES_SAVED, {
    onData: ({ data: { data }, client }) => {
      const unsavedValue = (itemPropertyLens?.values |> sequence)?.find(x => (x.propertyValueId |> get) === null);
      const propertyValues = data.propertyValuesSaved.propertyValues.map(x => ({
        ...x,
        inEdit: false,
        editValue: x.value
      }));

      unsavedValue
        ? resetItemPropertyLens(prev => ({ ...prev, values: [unsavedValue |> get, ...propertyValues] }))
        : resetItemPropertyLens(prev => ({ ...prev, values: propertyValues }));

      propertyValues.forEach(value =>
        addCachedDetail(
          client,
          { name: 'propertyValues', value: PROPERTY_VALUES_QUERY },
          { itemId: itemPropertyLens.itemId |> get, propertyId: itemPropertyLens.propertyId |> get },
          x => x.propertyValueId === value.propertyValueId,
          value,
          ['propertyValueId']
        )
      );

      addToast(t('Items.Values.ValueSaved'), 'success');
    }
  });
  // #endregion

  // #region handlers
  const handleSaveProperty = useCallback(() => {
    if (!validate(itemPropertyLens |> get)) return;
    (itemPropertyLens.saving |> set)(true);
    savePropertyGeneralData({
      variables: {
        input: {
          itemId: itemPropertyLens.itemId |> get,
          propertyId: itemPropertyLens.propertyId |> get,
          mandatory: itemPropertyLens.mandatory |> get,
          comments: itemPropertyLens.comments |> get,
          propertyValueTypeId: itemPropertyLens.propertyValueTypeId |> get,
          defaultValue:
            (itemPropertyLens.defaultValue |> get) instanceof Date
              ? (itemPropertyLens.defaultValue |> get)?.toLocaleDateString()
              : (itemPropertyLens.defaultValue |> get)?.toString(),
          isNew: isNewProperty
        }
      }
    });
  }, [isNewProperty, itemPropertyLens, savePropertyGeneralData, validate]);

  const handleAddNewValue = useCallback(() => {
    resetItemPropertyLens(prev => ({
      ...prev,
      values: [
        {
          propertyValueId: null,
          value: null,
          propertyMinValue: null,
          propertyMaxValue: null,
          inEdit: true,
          editValue: null
        },
        ...prev.values
      ]
    }));
  }, [resetItemPropertyLens]);

  const handleSaveValue = useCallback(
    (value, callback) => {
      savePropertyValue({
        variables: {
          input: {
            itemId: itemPropertyLens.itemId |> get,
            propertyId: itemPropertyLens.propertyId |> get,
            propertyValueId: value.propertyValueId,
            propertyMinValue: value.propertyMinValue?.toString(),
            propertyMaxValue: value.propertyMaxValue?.toString(),
            value: value.editValue instanceof Date ? value.editValue.toLocaleDateString() : value.editValue.toString()
          }
        }
      });
      callback();
    },
    [itemPropertyLens.itemId, itemPropertyLens.propertyId, savePropertyValue]
  );

  const handleDeletePropertyValue = useCallback(
    propertyValueId => {
      deletePropertyValue({
        variables: {
          input: {
            itemId: itemPropertyLens.itemId |> get,
            propertyId: itemPropertyLens.propertyId |> get,
            propertyValueId: propertyValueId
          }
        }
      });
    },
    [deletePropertyValue, itemPropertyLens.itemId, itemPropertyLens.propertyId]
  );

  const handleCancelEditValue = useCallback(() => {
    resetItemPropertyLens(prev => {
      prev.values.splice(0, 1);
      return { ...prev, values: prev.values };
    });
  }, [resetItemPropertyLens]);

  const handleLoadPropertyDetails = useCallback(async () => {
    if (!(itemPropertyLens.propertyId |> get)) return;
    const { data } = await clientQuery(PROPERTY_DETAILS, {
      variables: {
        propertyId: itemPropertyLens.propertyId |> get
      }
    });
    if (data) {
      setPropertyDetails(data.propertyDetails);
    }
  }, [clientQuery, itemPropertyLens.propertyId]);

  const handleImportValues = useCallback(
    newSelections => {
      if (!(itemPropertyLens.itemId |> get) || !(itemPropertyLens.propertyId |> get) || newSelections.length === 0) {
        return;
      }
      const input = newSelections.map(x => ({
        itemId: itemPropertyLens.itemId |> get,
        propertyId: itemPropertyLens.propertyId |> get,
        propertyValueId: null,
        propertyMinValue: null,
        propertyMaxValue: null,
        value: x
      }));
      savePropertyValues({
        variables: {
          input: input
        }
      });
    },
    [itemPropertyLens.itemId, itemPropertyLens.propertyId, savePropertyValues]
  );

  const handleSelectionChanged = useCallback(
    selectedValue => {
      savePropertyGeneralData({
        variables: {
          input: {
            itemId: itemPropertyLens.itemId |> get,
            propertyId: itemPropertyLens.propertyId |> get,
            mandatory: itemPropertyLens.mandatory |> get,
            comments: itemPropertyLens.comments |> get,
            propertyValueTypeId: itemPropertyLens.propertyValueTypeId |> get,
            defaultValue: selectedValue,
            isNew: isNewProperty
          }
        }
      });
    },
    [
      isNewProperty,
      itemPropertyLens.comments,
      itemPropertyLens.itemId,
      itemPropertyLens.mandatory,
      itemPropertyLens.propertyId,
      itemPropertyLens.propertyValueTypeId,
      savePropertyGeneralData
    ]
  );
  // #endregion

  useEffect(() => {
    validate(get(itemPropertyLens), itemPropertyDirtyInfo);
  }, [validate, itemPropertyLens, itemPropertyDirtyInfo]);

  const properties = useMemo(() => {
    return propertiesData?.properties?.map(property => ({
      ...property,
      propertyName: `${property.propertyCode} / ${property.propertyName}`
    }));
  }, [propertiesData?.properties]);

  const propertyValueTypes = useMemo(
    () => propertyValueTypesData?.propertyValueTypes.filter(value => value.isActive),
    [propertyValueTypesData?.propertyValueTypes]
  );

  return (
    <>
      <PropertyGeneralDataComponent
        lens={itemPropertyLens}
        validation={validation}
        isDirty={isDirty}
        properties={properties}
        propertyValueTypes={propertyValueTypes}
        onSave={handleSaveProperty}
        isNewProperty={isNewProperty}
      />
      {get(itemPropertyLens.propertyValueTypeCode) !== pvtUnspecified && (
        <PropertyValuesComponent
          lens={itemPropertyLens.values}
          propertyTypeCode={itemPropertyLens.propertyTypeCode |> get}
          propertyValueTypeCode={itemPropertyLens.propertyValueTypeCode |> get}
          propertyDetails={propertyDetails}
          defaultValue={itemPropertyLens.defaultValue |> get}
          isNewProperty={isNewProperty}
          onAddNewValue={handleAddNewValue}
          onSaveValue={handleSaveValue}
          onDeletePropertyValue={handleDeletePropertyValue}
          onCancelEditValue={handleCancelEditValue}
          onLoadPropertyDetails={handleLoadPropertyDetails}
          onImportValues={handleImportValues}
          onSelectionChanged={handleSelectionChanged}
        />
      )}
    </>
  );
};

ItemPropertyEditContainer.propTypes = {
  itemProperties: PropTypes.array
};

export default ItemPropertyEditContainer;
